import { POLARIUM_SYMBOL_RE } from './broker';
import type { DurationUnit } from './contracts';

/**
 * Traderoom: free Sobe/Desce entries outside the round clocks, traded as Polarium
 * **Digital** options (spot strike). Digital expires on the broker's fixed
 * expirations of each period (1, 5, 15 min…): the option bought now expires at
 * the period's next expiration still open for purchase, not "N minutes after
 * the click". There are no tick expiries on Polarium.
 */
export type ExpiryMode = 'time' | 'ticks';

/** Digital periods offered (seconds). The live list per asset comes from contracts_for.digital_periods. */
export const DIGITAL_TIME_PRESETS = [60, 300, 900] as const;

export const FREE_EXPIRY_LIMITS = {
  /** Kept for the shape of the order ticket: no tick expiries on Polarium. */
  ticks: { min: 0, max: 0 },
  /** Seconds. */
  time: { min: DIGITAL_TIME_PRESETS[0], max: DIGITAL_TIME_PRESETS[DIGITAL_TIME_PRESETS.length - 1]! },
} as const;

/** Time presets offered in the order panel (seconds). Default is 1 minute. */
export const FREE_TIME_PRESETS = DIGITAL_TIME_PRESETS;
export const FREE_DEFAULT_SECONDS = 60;
export const FREE_DEFAULT_TICKS = 0;

/** Candle widths offered on the Traderoom chart (seconds). 1 minute is the default. */
export const TRADEROOM_GRANULARITIES = [60, 300, 900, 1800] as const;
export type TraderoomGranularity = (typeof TRADEROOM_GRANULARITIES)[number];

/** Broker duration for an expiry: whole minutes go as `m`, the rest as `s`. */
export function freeDuration(_mode: ExpiryMode, value: number): { duration: number; durationUnit: DurationUnit } {
  return value % 60 === 0 ? { duration: value / 60, durationUnit: 'm' } : { duration: value, durationUnit: 's' };
}

/** Contract length in seconds (null for tick contracts). */
export function durationSeconds(duration: number, unit: DurationUnit): number | null {
  switch (unit) {
    case 's':
      return duration;
    case 'm':
      return duration * 60;
    case 'h':
      return duration * 3600;
    case 'd':
      return duration * 86_400;
    default:
      return null;
  }
}

/** Error message for an expiry the broker does not offer, or null when valid. */
export function validateFreeDuration(duration: number, unit: DurationUnit): string | null {
  if (!Number.isInteger(duration) || duration < 1) return 'Expiração inválida.';
  const s = durationSeconds(duration, unit);
  return s !== null && (DIGITAL_TIME_PRESETS as readonly number[]).includes(s) ? null : 'Na Digital a expiração é de 1, 5 ou 15 minutos.';
}

/** Short pt-BR label: 1 min, 5 min, 1 h. */
export function expiryLabel(mode: ExpiryMode, value: number): string {
  if (mode === 'ticks') return `${value} tick${value === 1 ? '' : 's'}`;
  if (value < 60) return `${value}s`;
  if (value % 3600 === 0) return `${value / 3600} h`;
  const m = Math.floor(value / 60);
  const s = value % 60;
  return s ? `${m} min ${s}s` : `${m} min`;
}

// ---------------------------------------------------------------- assets

/** Traderoom assets: every Polarium ticker with Digital open (EURUSD-OTC, GBPUSD, …). */
export function isTraderoomSymbol(symbol: string): boolean {
  return POLARIUM_SYMBOL_RE.test(symbol);
}

/** OTC pairs trade around the clock (weekends included); regular markets have hours. */
export function isOtcSymbol(symbol: string): boolean {
  return /-OTC$/.test(symbol);
}

export type AssetCategory = 'forex' | 'crypto' | 'stocks' | 'indices' | 'commodities';

/** Markets of the asset browser, in display order (the broker's own groups). */
export const ASSET_CATEGORIES: { key: AssetCategory; label: string }[] = [
  { key: 'forex', label: 'Forex' },
  { key: 'crypto', label: 'Cripto' },
  { key: 'stocks', label: 'Ações' },
  { key: 'indices', label: 'Índices' },
  { key: 'commodities', label: 'Commodities' },
];

export function assetCategory(market: string): AssetCategory | null {
  return ASSET_CATEGORIES.some((c) => c.key === market) ? (market as AssetCategory) : null;
}

/** Quadcode active type (Forex, Crypto, Stock, Index, Commodity) → market of the asset browser. */
export function categoryOfBrokerType(type: string): AssetCategory {
  switch (type.trim().toLowerCase()) {
    case 'crypto':
      return 'crypto';
    case 'stock':
    case 'stocks':
      return 'stocks';
    case 'index':
    case 'indices':
      return 'indices';
    case 'commodity':
    case 'commodities':
      return 'commodities';
    default:
      return 'forex';
  }
}

/** Group of an asset inside its market: the real market first, then OTC (weekends and nights). */
export function assetGroup(category: AssetCategory, otc: boolean): { key: string; label: string } {
  const label = ASSET_CATEGORIES.find((c) => c.key === category)?.label ?? 'Forex';
  return otc ? { key: `${category}_otc`, label: `${label} OTC` } : { key: category, label };
}

/** Group headings, in display order. */
export const ASSET_GROUPS: Record<string, string> = Object.fromEntries(
  ASSET_CATEGORIES.flatMap((c) => [
    [c.key, c.label],
    [`${c.key}_otc`, `${c.label} OTC`],
  ]),
);

/** Broker name → the name shown on screen. */
export function assetDisplayName(name: string): string {
  return name;
}

/** Expiries an asset offers in the Traderoom. */
export interface ExpiryLimits {
  symbol: string;
  /** Tick expiries: never on Polarium. */
  ticks: { min: number; max: number } | null;
  /** Time expiries in seconds, or null (Digital closed on this asset now). */
  time: { min: number; max: number } | null;
  /** Only these expiries (seconds) exist — the Digital periods open now. */
  presets?: number[];
}

// ---------------------------------------------------------------- products

/**
 * Polarium products traded in the Traderoom. Binária joins the broker's turbo options (1–5 min)
 * and binary options (15 min and longer): both expire at a fixed time.
 */
export type TraderoomProduct = 'blitz' | 'binary' | 'digital';

export const TRADEROOM_PRODUCTS: { key: TraderoomProduct; label: string; hint: string }[] = [
  { key: 'blitz', label: 'Blitz', hint: 'Expira em segundos, contados da compra' },
  { key: 'binary', label: 'Binária', hint: 'Expira num horário fixo' },
  { key: 'digital', label: 'Digital', hint: 'Expira num horário fixo; lucro conforme o preço' },
];

export function isTraderoomProduct(value: unknown): value is TraderoomProduct {
  return value === 'blitz' || value === 'binary' || value === 'digital';
}

/** What an asset offers right now, per product (the Traderoom order ticket). */
export interface TraderoomProducts {
  symbol: string;
  /** Expiries in seconds after the purchase, and the return (%) paid on a win. */
  blitz: { open: boolean; seconds: number[]; payout: number } | null;
  /** Fixed expiries still open for purchase: epoch seconds, purchase deadline, return (%). */
  binary: { open: boolean; expiries: { at: number; purchaseUntil: number; payout: number }[] } | null;
  /** Digital periods (60 = 1 min…) open now. */
  digital: { open: boolean; periods: number[] } | null;
}

/** Error message for a Traderoom expiry, or null when valid (the broker still checks it is open now). */
export function validateTraderoomExpiry(product: TraderoomProduct, duration: number, unit: DurationUnit, dateExpiry: number | undefined, nowS: number): string | null {
  if (product === 'digital') return validateFreeDuration(duration, unit);
  if (product === 'binary') {
    return dateExpiry !== undefined && Number.isInteger(dateExpiry) && dateExpiry > nowS ? null : 'Escolha um horário de expiração.';
  }
  const s = durationSeconds(duration, unit);
  return s !== null && Number.isInteger(s) && s >= 5 && s <= 86_400 ? null : 'Expiração do Blitz inválida.';
}
